import Player from '@vimeo/player';
import { useCallback, useEffect, useMemo, useRef } from 'react';

/** Extrai videoId e hash (para não listados) de uma URL do Vimeo */
function parseVimeoUrl(url: string): { videoId: string; hash?: string } | null {
  if (!url || typeof url !== 'string') return null;
  const t = url.trim();
  // player.vimeo.com/video/ID?h=HASH
  const playerWithHash = t.match(/player\.vimeo\.com\/video\/(\d+).*[?&]h=([a-zA-Z0-9]+)/i);
  if (playerWithHash) return { videoId: playerWithHash[1], hash: playerWithHash[2] };
  const playerId = t.match(/player\.vimeo\.com\/video\/(\d+)/i);
  if (playerId) return { videoId: playerId[1] };
  // vimeo.com/ID/HASH ou vimeo.com/ID/HASH?query
  const watch = t.match(/vimeo\.com\/(\d+)\/([a-zA-Z0-9]+)/i);
  if (watch) return { videoId: watch[1], hash: watch[2] };
  const watchIdOnly = t.match(/vimeo\.com\/(\d+)(?:\?|$)/i);
  if (watchIdOnly) return { videoId: watchIdOnly[1] };
  return null;
}

export interface VimeoPlayerProgress {
  /** Tempo assistido em segundos (soma do tempo em que o vídeo esteve tocando) */
  watchedTime: number;
  /** Percentual assistido (0-100) baseado na posição máxima alcançada */
  percentage: number;
  /** Posição atual em segundos (para retomar depois) */
  currentTime: number;
  /** Duração total do vídeo em segundos */
  duration: number;
  /** Indica se o vídeo foi considerado concluído (percentual >= completionThreshold) */
  completed: boolean;
}

export interface VimeoPlayerProps {
  /**
   * ID do vídeo Vimeo (ex: 123456789). Opcional se vimeoUrl for passado.
   */
  videoId?: string;
  /**
   * URL completa do Vimeo (ex: https://vimeo.com/1162610262/5f213af680).
   * Quando informada, videoId e hash são extraídos automaticamente.
   */
  vimeoUrl?: string;
  /**
   * Hash do vídeo não listado (ex: 5f213af680). Use junto com videoId quando
   * o backend retornar apenas id e hash separados.
   */
  unlistedHash?: string;
  /** Posição inicial em segundos - retomar de onde parou */
  initialTime?: number;
  /** Percentual inicial (0-100) para retomar quando não houver `initialTime` */
  initialProgressPercentage?: number;
  /** Percentual mínimo para considerar conclusão (0-100). Padrão: 95 */
  completionThreshold?: number;
  /** Intervalo em segundos para disparar onProgress. Padrão: 2 */
  progressInterval?: number;
  /** Margem em segundos além do tempo assistido permitida ao buscar. Padrão: 2 */
  seekTolerance?: number;
  /** Callback com progresso: tempo assistido, percentual, conclusão */
  onProgress?: (progress: VimeoPlayerProgress) => void;
  /** Callback quando o usuário tenta pular (avançar) além do permitido */
  onSkipAttempt?: () => void;
  /** Se true, bloqueia avanço do vídeo além do tempo já assistido. Padrão: true */
  blockSeek?: boolean;
  /** Classe CSS para o container do iframe */
  className?: string;
  /** Estilo para o container */
  style?: React.CSSProperties;
  /** Identificador de teste para o container do player */
  dataE2e?: string;
}

export default function VimeoPlayer({
  videoId: videoIdProp,
  vimeoUrl,
  unlistedHash,
  initialTime = 0,
  initialProgressPercentage = 0,
  completionThreshold = 95,
  progressInterval = 2,
  seekTolerance = 2,
  onProgress,
  onSkipAttempt,
  blockSeek = true,
  className,
  style,
  dataE2e,
}: VimeoPlayerProps) {
  const { videoId, hash } = useMemo(() => {
    if (vimeoUrl) {
      const parsed = parseVimeoUrl(vimeoUrl);
      if (parsed) return { videoId: parsed.videoId, hash: parsed.hash };
    }
    return {
      videoId: videoIdProp ?? '',
      hash: unlistedHash,
    };
  }, [vimeoUrl, videoIdProp, unlistedHash]);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player | null>(null);
  const lastWatchedTimeRef = useRef(0);
  const totalWatchedTimeRef = useRef(0);
  const lastProgressEmitRef = useRef(0);
  const isSeekingRef = useRef(false);
  const durationRef = useRef(0);
  const lastSkipAttemptRef = useRef(0);

  const emitProgress = useCallback(
    (currentTime: number, watchedTime: number, durationSeconds: number) => {
      if (durationSeconds <= 0) return;
      const percentage = Math.min(
        100,
        (lastWatchedTimeRef.current / durationSeconds) * 100
      );
      const completed = percentage >= completionThreshold;
      onProgress?.({
        watchedTime: totalWatchedTimeRef.current,
        percentage,
        currentTime,
        duration: durationSeconds,
        completed,
      });
    },
    [completionThreshold, onProgress]
  );

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !videoId) return;

    const player = new Player(iframe);
    playerRef.current = player;

    let lastTime = 0;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const enforceMaxTime = (currentSeconds: number) => {
      if (!blockSeek) return false;
      const maxAllowed = lastWatchedTimeRef.current + seekTolerance;
      if (currentSeconds <= maxAllowed) return false;
      player.setCurrentTime(lastWatchedTimeRef.current);
      const now = Date.now();
      if (now - lastSkipAttemptRef.current > 1000) {
        lastSkipAttemptRef.current = now;
        onSkipAttempt?.();
      }
      return true;
    };

    const handleTimeUpdate = (data: { seconds: number }) => {
      const { seconds } = data;

      // Sempre aplicar limite primeiro (mesmo durante seeking – timeupdate pode vir antes de seeking)
      if (enforceMaxTime(seconds)) return;

      if (isSeekingRef.current) return;

      const delta = seconds - lastTime;
      const isLikelySeek = delta > 0 && delta >= 10; // pulo grande = seek, não avanço natural

      if (delta > 0 && delta < 10) {
        totalWatchedTimeRef.current += delta;
      }
      lastTime = seconds;

      // Só avançar lastWatchedTimeRef com reprodução real, nunca com seek
      if (!isLikelySeek && seconds > lastWatchedTimeRef.current) {
        lastWatchedTimeRef.current = seconds;
      }

      const now = Date.now() / 1000;
      if (onProgress && now - lastProgressEmitRef.current >= progressInterval) {
        lastProgressEmitRef.current = now;
        emitProgress(seconds, totalWatchedTimeRef.current, durationRef.current);
      }
    };

    const handleSeeking = () => {
      isSeekingRef.current = true;
    };

    const handleSeeked = (data: { seconds: number }) => {
      const { seconds } = data;
      isSeekingRef.current = false;
      enforceMaxTime(seconds);
    };

    player.on('timeupdate', handleTimeUpdate);
    player.on('seeked', handleSeeked);
    player.on('seeking', handleSeeking);

    const seekToInitial = () => {
      if (initialTime > 0 && lastWatchedTimeRef.current === 0) {
        lastWatchedTimeRef.current = initialTime;
        player.setCurrentTime(initialTime);
        return;
      }
      if (lastWatchedTimeRef.current > 0) return;
      const boundedPercentage = Math.max(0, Math.min(initialProgressPercentage, 100));
      if (boundedPercentage <= 0) return;
      player.getDuration().then(duration => {
        if (duration <= 0 || lastWatchedTimeRef.current > 0) return;
        const nextTime = (boundedPercentage / 100) * duration;
        lastWatchedTimeRef.current = nextTime;
        player.setCurrentTime(nextTime);
      });
    };

    const setupDuration = () => {
      player.getDuration().then(d => {
        durationRef.current = d;
      });
    };

    player.ready().then(() => {
      setupDuration();
      seekToInitial();
    });

    // Poll mais frequente quando blockSeek: detecta e corrige pulo rápido
    const pollMs = blockSeek ? 300 : 1000;
    intervalId = setInterval(() => {
      player.getCurrentTime().then(seconds => {
        handleTimeUpdate({ seconds });
      });
    }, pollMs);

    return () => {
      if (intervalId) clearInterval(intervalId);
      player.off('timeupdate', handleTimeUpdate);
      player.off('seeked', handleSeeked);
      player.off('seeking', handleSeeking);
      playerRef.current = null;
    };
  }, [
    videoId,
    blockSeek,
    seekTolerance,
    progressInterval,
    initialTime,
    initialProgressPercentage,
    onSkipAttempt,
    emitProgress,
    onProgress,
  ]);

  const embedUrl =
    hash != null && hash !== ''
      ? `https://player.vimeo.com/video/${videoId}?h=${hash}`
      : `https://player.vimeo.com/video/${videoId}`;

  if (!videoId) return null;

  const containerStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    minHeight: 0,
    maxHeight: '100%',
    flex: 1,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'stretch',
    ...style,
  };

  const iframeWrapperStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    minHeight: 0,
    aspectRatio: '16/9',
    position: 'relative',
    flex: 1,
  };

  return (
    <div
      className={className}
      style={containerStyle}
      data-e2e={dataE2e}
    >
      <div style={iframeWrapperStyle}>
        <iframe
          ref={iframeRef}
          src={embedUrl}
          title="Vimeo player"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          referrerPolicy="origin"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
      </div>
    </div>
  );
}
